// Paired file: crewbit-admin/src/lib/paymentSchedule.ts must remain byte-identical.
// Per feedback_admin_pair_must_match_mobile_body.md — there is no shared package
// between the two repos; the cross-surface money pattern is paired files.
//
// derivePaymentSchedule resolves a quote's payment_schedule_items into per-trigger
// totals. The percent-base is quotes.total (post-tax) — mirrors the server-side
// approve_quote_by_token deposit derivation exactly:
//   round(quote.total * percent / 100, 2)
// (See migration 330 — approve_quote_by_token deposit cascade, derived from the
// on_signature row.)
//
// Q2 Phase 2a (§T.108).

export type PaymentScheduleItem = {
  id: string;
  sort_order: number;
  description: string | null;
  amount_type: 'percent' | 'fixed';
  amount: number;
  trigger_type: 'on_signature' | 'on_date' | 'on_job_complete' | 'manual';
  trigger_date: string | null;
  status: 'pending' | 'invoiced' | 'paid' | 'skipped';
  invoice_id: string | null;
};

export type DerivedPaymentSchedule = {
  depositTotal: number;
  progressTotal: number;
  finalTotal: number;
  scheduledTotal: number;
  manualTotal: number;
  totalScheduled: number;
  resolvedItems: Array<PaymentScheduleItem & { resolvedAmount: number }>;
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function resolveAmount(item: PaymentScheduleItem, quoteTotal: number): number {
  if (item.amount_type === 'fixed') return round2(Number(item.amount) || 0);
  return round2((quoteTotal * (Number(item.amount) || 0)) / 100);
}

export function derivePaymentSchedule(
  items: PaymentScheduleItem[],
  quoteTotal: number,
): DerivedPaymentSchedule {
  const resolvedItems = items
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((item) => ({ ...item, resolvedAmount: resolveAmount(item, quoteTotal) }));

  let depositTotal = 0;
  let progressTotal = 0;
  let finalTotal = 0;
  let scheduledTotal = 0;
  let manualTotal = 0;

  for (const item of resolvedItems) {
    if (item.trigger_type === 'on_signature') depositTotal += item.resolvedAmount;
    else if (item.trigger_type === 'on_job_complete') {
      // Convention: the LAST on_job_complete row is "final"; earlier on_job_complete
      // rows are "progress." Mirrors today's 3-part deposit/progress/final shape
      // when a quote uses the canonical 3-row schedule.
      progressTotal += item.resolvedAmount;
    } else if (item.trigger_type === 'on_date') scheduledTotal += item.resolvedAmount;
    else if (item.trigger_type === 'manual') manualTotal += item.resolvedAmount;
  }

  // Promote the last on_job_complete row from progress → final (3-part convention).
  for (let i = resolvedItems.length - 1; i >= 0; i--) {
    if (resolvedItems[i].trigger_type === 'on_job_complete') {
      finalTotal = resolvedItems[i].resolvedAmount;
      progressTotal -= finalTotal;
      break;
    }
  }

  const totalScheduled = depositTotal + progressTotal + finalTotal + scheduledTotal + manualTotal;

  return {
    depositTotal: round2(depositTotal),
    progressTotal: round2(progressTotal),
    finalTotal: round2(finalTotal),
    scheduledTotal: round2(scheduledTotal),
    manualTotal: round2(manualTotal),
    totalScheduled: round2(totalScheduled),
    resolvedItems,
  };
}

// Convenience: extract the on_signature deposit amount only (mirrors the server
// derivation in approve_quote_by_token — LIMIT 1 ORDER BY sort_order).
export function deriveOnSignatureDeposit(
  items: PaymentScheduleItem[],
  quoteTotal: number,
): number {
  const sigRow = items
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .find((i) => i.trigger_type === 'on_signature');
  if (!sigRow) return 0;
  return resolveAmount(sigRow, quoteTotal);
}

// Helper: construct the canonical 3-row schedule (deposit/progress/final) from
// percentages. Used by QuoteBuilderSheet (mobile) + admin updatePaymentSchedule
// to emit a payment_schedule_items array the create_quote /
// set_payment_schedule_items RPCs accept.
//
// The 3-row convention: deposit on_signature (idx 0) + progress on_job_complete
// (idx 1) + final on_job_complete (idx 2). derivePaymentSchedule promotes the
// LAST on_job_complete row to "final," keeping the 3-part read-side shape.
export type CanonicalScheduleInput = {
  depositPercentage: number;
  progressPercentage: number;
  finalPercentage: number;
};

export function buildCanonicalSchedule(input: CanonicalScheduleInput): Array<{
  sort_order: number;
  description: string;
  amount_type: 'percent';
  amount: number;
  trigger_type: 'on_signature' | 'on_job_complete';
}> {
  return [
    { sort_order: 0, description: 'Deposit', amount_type: 'percent', amount: input.depositPercentage, trigger_type: 'on_signature' },
    { sort_order: 1, description: 'Progress', amount_type: 'percent', amount: input.progressPercentage, trigger_type: 'on_job_complete' },
    { sort_order: 2, description: 'Final', amount_type: 'percent', amount: input.finalPercentage, trigger_type: 'on_job_complete' },
  ];
}

// Inverse of buildCanonicalSchedule — extract the 3 percentages from an existing
// schedule for editor UIs (QuoteSection admin). Returns 25/50/25 defaults if
// the schedule is empty or doesn't fit the canonical 3-row shape.
export function extractCanonicalPercentages(items: PaymentScheduleItem[]): CanonicalScheduleInput {
  const sorted = items.slice().sort((a, b) => a.sort_order - b.sort_order);
  const dep = sorted.find((i) => i.trigger_type === 'on_signature' && i.amount_type === 'percent');
  const completes = sorted.filter((i) => i.trigger_type === 'on_job_complete' && i.amount_type === 'percent');
  const final = completes.length > 0 ? completes[completes.length - 1] : undefined;
  const progress = completes.length > 1 ? completes[0] : undefined;
  return {
    depositPercentage:  dep      ? Number(dep.amount)      : 25,
    progressPercentage: progress ? Number(progress.amount) : 50,
    finalPercentage:    final    ? Number(final.amount)    : 25,
  };
}

// ── Payment progress (Client-App §10 / Layer-2 P5) ──────────────────────────
// The client project-detail "paid / due / upcoming" view, derived from the
// payment_schedule_items' own status (status IS the invoices/payments join:
// 'paid' once the item's invoice is paid, 'invoiced' once billed, 'pending'
// before). Composes on derivePaymentSchedule for the resolved amounts + the
// deposit/progress/final roles — one amount source of truth.

export type PaymentProgressBucket = 'paid' | 'due' | 'upcoming';
export type PaymentRole = 'deposit' | 'progress' | 'final' | 'scheduled' | 'manual';

export type PaymentProgressRow = {
  id: string;
  description: string | null;
  role: PaymentRole;
  amount: number;
  status: PaymentScheduleItem['status'];
  /** null for 'skipped' rows (excluded from every total). */
  bucket: PaymentProgressBucket | null;
  invoiceId: string | null;
};

export type DerivedPaymentProgress = {
  paidTotal: number;
  dueTotal: number;
  upcomingTotal: number;
  rows: PaymentProgressRow[];
  /** The underlying deposit/progress/final totals (same amount basis). */
  schedule: DerivedPaymentSchedule;
};

export function derivePaymentProgress(
  items: PaymentScheduleItem[],
  quoteTotal: number,
): DerivedPaymentProgress {
  const schedule = derivePaymentSchedule(items, quoteTotal);
  const resolved = schedule.resolvedItems; // already sort_order-ascending

  // The LAST on_job_complete row is "final" (mirrors derivePaymentSchedule's
  // promotion); earlier on_job_complete rows are "progress".
  let lastJobCompleteIdx = -1;
  for (let i = resolved.length - 1; i >= 0; i--) {
    if (resolved[i].trigger_type === 'on_job_complete') { lastJobCompleteIdx = i; break; }
  }

  const bucketOf = (status: PaymentScheduleItem['status']): PaymentProgressBucket | null =>
    status === 'paid' ? 'paid' : status === 'invoiced' ? 'due' : status === 'pending' ? 'upcoming' : null;

  const roleOf = (item: PaymentScheduleItem, idx: number): PaymentRole => {
    if (item.trigger_type === 'on_signature') return 'deposit';
    if (item.trigger_type === 'on_job_complete') return idx === lastJobCompleteIdx ? 'final' : 'progress';
    if (item.trigger_type === 'on_date') return 'scheduled';
    return 'manual';
  };

  let paidTotal = 0;
  let dueTotal = 0;
  let upcomingTotal = 0;

  const rows: PaymentProgressRow[] = resolved.map((item, idx) => {
    const bucket = bucketOf(item.status);
    if (bucket === 'paid') paidTotal += item.resolvedAmount;
    else if (bucket === 'due') dueTotal += item.resolvedAmount;
    else if (bucket === 'upcoming') upcomingTotal += item.resolvedAmount;
    return {
      id: item.id,
      description: item.description,
      role: roleOf(item, idx),
      amount: item.resolvedAmount,
      status: item.status,
      bucket,
      invoiceId: item.invoice_id,
    };
  });

  return {
    paidTotal: round2(paidTotal),
    dueTotal: round2(dueTotal),
    upcomingTotal: round2(upcomingTotal),
    rows,
    schedule,
  };
}
